import { PayloadAction } from '@reduxjs/toolkit';
import { all, call, delay, fork, race, select, put, take, takeEvery } from 'redux-saga/effects';
import { RootState } from 'types';
import {
  IPlayerState,
  IViewer,
  IVideoState,
  updatePlayerState,
  updateVideoState,
  updateViewerState,
  VideoPlayState,
  ViewerState,
} from 'core/slices/player';
import { EntityTypes } from 'constants/entities';
import { ActionBehaviour, IActionBasic } from 'types/domain/action';
import { selectAction, selectActionVoiceSceneId } from 'core/selectors/product/action/action';
import { IUpdateState, updateState } from 'core/actions/product/state';
import { selectSceneAsset } from 'core/selectors/product/scene/sceneAsset';
import { selectSceneConfig, selectVoiceSceneConfig } from 'core/selectors/product/scene/sceneConfig';
import { ISceneConfig, ISceneDAO, SceneType } from 'types/domain/scene';
import { AssetType, IAsset } from 'types/app/asset';
import { loadAsset } from 'core/services/asset';
import { LoadingState } from 'types/domain/base';
import { dashPlayerEvents, playheadStillFor, VideoEvents, videoPlaybackEvents } from 'utils/videoEvents';
import { isIOS, isSafari } from 'react-device-detect';
import { selectScene } from 'core/selectors/product/scene/scene';
import { IActionFeed, selectActionFeed } from 'core/selectors/product/action/actionFeed';
import {
  selectActionEndTime,
  selectActionSceneEnd,
  selectSceneRunEnd,
} from 'core/selectors/product/action/actionEndTime';
import { selectActionEditor } from 'core/slices/actionEditor/selectActionEditor';
import { actionEditorActions, IActionEditorState } from 'core/slices/actionEditor';
import { loaders } from '../product/assets/load';
import { dashjs, dashStreamingSettings } from 'utils/loadDash';
import { unloadAsset } from 'core/slices/assets';

import { prev } from 'core/slices/register';
import { selectSessionMode } from 'core/selectors/admin/sessionMode';
import { canPlayVideo } from './play';
import { Mode } from 'core/slices/session';

let DashPlayer = null;

// How long manageVideo waits with zero playback events before treating the
// element as wedged (see the watchdog in manageVideo). A healthy element floods
// the channel with `timeupdate` (~30ms) while playing, and any load/seek emits
// canplay/progress/seeked — so a full silent window only happens when dash has
// wedged (readyState parked, no events). Kept comfortably above one network
// round-trip so genuinely slow-but-progressing loads never trip it.
const READY_WATCHDOG_MS = 3000;
// Silent windows to nudge with a seek before escalating to a full source rebuild.
const READY_WATCHDOG_NUDGES = 2;
// Cap on full source rebuilds so a truly unplayable asset can't reload forever.
const READY_WATCHDOG_MAX_RELOADS = 2;
// How close to where the video can stop a stalled element must be for the stall to
// count as the end. dash encodes often end ~0.1s short of the declared duration, so
// the playhead parks just shy of `end` and an exact `>=` never trips.
const STALLED_END_TOLERANCE = 0.3;
// A video still "playing" whose playhead hasn't moved for this long is frozen.
const FROZEN_MS = 1000;
// How close to the end a frozen playhead must be to count as the end. Wider than
// STALLED_END_TOLERANCE: iOS plays HLS natively, and the stream can run out a little
// short of the duration it declares — the element then freezes there without pausing
// or emitting `ended`/`waiting`.
const FROZEN_END_TOLERANCE = 1;
// Silent windows in which we retry play() on a video that stopped on its own
// mid-action, before handing control back to the learner via the play button.
const STALLED_RESUME_ATTEMPTS = 2;
// Slack when comparing an action's end against the end baked into the loaded source.
const SOURCE_END_EPSILON = 0.05;
// How long an `emptied` element has to get a source back before it counts as unloaded.
const EMPTIED_GRACE = 1000;

// Tracks the asset whose end-of-video relay we've already started, so the two
// independent end detectors (manageVideo's <video> events and dashEvents'
// playbackTimeUpdated) can't both pass the `!ended` check and double-fire the
// next-action transition. Reset whenever `ended` is set back to false (see
// removeVideo / startVideo) so a replayed or shared-scene video can end again.
let endedAssetId: string | null = null;

// True while a scene's video source is being (re)attached to the shared #videoEl.
// dash fires an `emptied` event during reattach even though the element keeps a
// valid source and plays; without this guard the EMPTIED handler nulls
// `video.assetId`, which flips selectSceneLoaded to false and hides the (correct,
// playing) video mesh → black. Cleared once the element reports it can play.
let attachingSource = false;

// The `#t=…,end` baked into the currently loaded source. iOS stops natively at it and
// dash turns it into a SourceBuffer append window, so the element can never play past
// it — an action on the same scene that ends later needs the source rebuilt.
let loadedSource: { assetId: string | null; end: number | null } = { assetId: null, end: null };

// Bumped by each manageVideo so a superseded loop (e.g. the same asset reloaded for a
// wider source window) stands down instead of handling the element's events twice.
let manageGeneration = 0;

// With a voiceover, #videoEl carries the voiceover and the scene video plays on the
// muted #bgVideoEl instead (sagas/player/backgroundVideo) — never load it here.
function* sceneVideoIsBackground() {
  const voiceSceneId: number | undefined = yield select(state => selectActionVoiceSceneId(state, undefined));
  return voiceSceneId != null;
}

function* assetLoaded(action) {
  if (yield call(sceneVideoIsBackground)) return;
  const sceneAsset: IAsset = yield select(state => selectSceneAsset(state, undefined));
  const videoState: IVideoState = yield select((state: RootState) => state.core.player.state.video);

  if (
    sceneAsset &&
    action.meta.arg.id === sceneAsset.id &&
    sceneAsset.type === AssetType.VIDEO &&
    videoState.assetId !== action.meta.arg.id
  )
    yield loadSceneVideo(sceneAsset, 'assetLoaded');
}

const hasLiveSource = (videoEl: HTMLVideoElement) =>
  !!videoEl && !!(videoEl.currentSrc || videoEl.src) && videoEl.readyState > 0;

function* loadSceneVideo(asset: IAsset, caller?: string, recoveryAttempt = 0) {
  if (asset.type !== AssetType.VIDEO) return console.warn('trying to load an asset that isnt video');

  const videoEl: HTMLVideoElement = document.getElementById('videoEl') as HTMLVideoElement;
  const videoState: IVideoState = yield select((state: RootState) => state.core.player.state.video);
  // A matching `assetId` is not proof the element still has a live source: after
  // returning from a no-video scene the dash source can be torn down (SourceBuffer
  // removed) while `assetId` stays set, so the normal guard would skip reattaching
  // and leave the texture black. Treat "no decodable source on the element" as a
  // mismatch too, forcing removeVideo → full reload.
  if (asset.id !== videoState.assetId || !hasLiveSource(videoEl)) yield removeVideo('initial load remove');

  yield put(yield call(updateVideoState, { assetId: asset.id, ready: false }));
  // A voiceover's source is cut by the voiceover's config, not the scene it plays over.
  const voiceSceneId: number | undefined = yield select(state => selectActionVoiceSceneId(state, undefined));
  const config: ISceneConfig = yield select(state =>
    voiceSceneId != null && voiceSceneId === asset.entityId
      ? selectVoiceSceneConfig(state, voiceSceneId, undefined)
      : selectSceneConfig(state, undefined, undefined),
  );
  // dash turns the `#t=start,end` fragment's `end` into a fixed SourceBuffer
  // append window. In the editor the end is being edited and the timeline scrubs
  // freely, so a baked-in end both goes stale on edit and blocks seeking past it
  // (→ "Failed to set append window" / byteLength append errors). Omit `end`
  // there and let the saga stop playback at the live edited end instead; the
  // player keeps the end for its exact, native stop.
  const actionEditor: IActionEditorState = yield select(selectActionEditor);
  //
  // The end must also cover every following action on this scene: moving between them
  // only seeks, so a source cut at the first action's end stops the next one dead
  // (iOS pauses at the fragment end, dash starves at the append window) while its own
  // end is still ahead — the video freezes and never ends.
  const runEnd: number | null | undefined = yield select(state => selectSceneRunEnd(state, asset.sceneId));
  const end =
    actionEditor?.enabled || !config?.end
      ? null
      : runEnd === undefined
      ? config.end
      : runEnd === null
      ? null
      : Math.max(runEnd, config.end);
  const hasStartOrEnd = config?.start || config?.end;
  const tFragment = hasStartOrEnd ? `#t=${config?.start ?? 0}${end ? `,${end}` : ''}` : '';
  const configuredUrl = `${asset.url}${tFragment}`;
  loadedSource = { assetId: asset.id, end };

  // Guard the upcoming source (re)attach: dash emits `emptied` mid-attach, which
  // must not be treated as an unload (see EMPTIED handler in manageVideo).
  attachingSource = true;

  if (isIOS || isSafari) {
    videoEl.src = configuredUrl;
    videoEl.load();
  } else yield fork(loadDash, asset, configuredUrl);

  //mute while managing
  videoEl.muted = true;

  yield manageVideo(asset, recoveryAttempt);
}

// Rebuild a still-live source whose baked-in end is too short — a scenario branch
// picked after it loaded adds a following action that plays on past that end. dash
// can't move the append window of a live SourceBuffer ("Failed to set append window"):
// the stream tears down, the element empties and the slide goes black for good. So
// tear it down ourselves first and load clean, as the ready watchdog does.
function* rebuildSource(asset: IAsset, cause = 'action ends past loaded source') {
  yield removeVideo(cause);
  yield loadSceneVideo(asset, cause);
}

function* loadDash(asset: IAsset, url) {
  const videoEl: HTMLVideoElement = document.getElementById('videoEl') as HTMLVideoElement;
  // The caller's asset can be stale: the watchdog rebuild runs removeVideo first, whose
  // unloadAsset destroys the loader and resets `loading`, yet hands us the snapshot it
  // started with. Read the live entry, and start the load ourselves if nothing else
  // will — otherwise we either fall through to an unattached player or wait on a
  // fulfilled that never comes.
  asset = yield select((state: RootState) => state.core.assets[asset.id] ?? asset);
  if (![LoadingState.LOADED, LoadingState.LOADING].includes(asset.loading)) yield put(yield call(loadAsset, asset));

  while (asset.loading !== LoadingState.LOADED) {
    yield take(loadAsset.fulfilled);
    // eslint-disable-next-line no-loop-func
    asset = yield select((state: RootState) => state.core.assets[asset.id]);
  }

  if (loaders[asset.id]) {
    if (!loaders[asset.id].attachView) return; //console.warn('no attachView for', asset.id);

    // Reattaching a preloaded dash player to the shared #videoEl leaves its segment
    // scheduler stale: on a later switch dash can't generate a media request for the
    // start segment and falls back to seeking near the end (readyState stuck at 1 →
    // black, "Adjusting playback time … because of gap in the manifest"). Calling
    // attachView + attachSource alone does NOT clear that state — the player is
    // fresh on first play, so it works once and wedges on every revisit.
    //
    // reset() tears the streaming stack down properly; attachView + attachSource
    // then rebuild a clean scheduler for this element. (attachView must come first
    // so the source binds to #videoEl.)
    if (loaders[asset.id].reset) yield call([loaders[asset.id], loaders[asset.id].reset]);

    // reset() also drops the player's settings back to dash's defaults, so the
    // creation-time settings must be re-applied here, before re-attaching — the
    // shared object is the single source of truth for what those are (see
    // dashStreamingSettings for why each one matters).
    yield call([loaders[asset.id], loaders[asset.id].updateSettings], dashStreamingSettings);

    yield call(loaders[asset.id].attachView, videoEl);
    yield call([loaders[asset.id], loaders[asset.id].attachSource], url);
    const mode: Mode = yield select(selectSessionMode);
    if (loaders[asset.id].isReady() && mode === Mode.ADMIN) yield startVideo();
    return;
  }

  if (DashPlayer == null) {
    const dash = yield call(dashjs);
    DashPlayer = yield call(dash.MediaPlayer().create);
    yield call([DashPlayer, DashPlayer.updateSettings], dashStreamingSettings);
    yield fork(dashEvents, DashPlayer, dash.MediaPlayer);
    if (videoEl) yield call([DashPlayer, DashPlayer.initialize], videoEl, null, true);
  }

  try {
    yield call([DashPlayer, DashPlayer.attachSource], url);
  } catch (e) {}
}
function* dashEvents(DashPlayer, MediaPlayer) {
  const videoEvents = yield call(dashPlayerEvents, DashPlayer, MediaPlayer);
  const assetId = yield select((state: RootState) => state.core.player.state.video.assetId);
  let aId = assetId;

  while (aId === assetId) {
    let event: Event = yield take(videoEvents);

    aId = yield select((state: RootState) => state.core.player.state.video.assetId);

    if (event.type === 'error') {
      yield put(yield call(updateVideoState, { play: VideoPlayState.ERROR }));
      yield put(yield call(updatePlayerState, { paused: true, culprit: 'dashError' }));
    }

    if (event.type === 'streamTeardownComplete') {
      const sceneAsset: IAsset = yield select(state => selectSceneAsset(state, undefined));
      const videoState: IVideoState = yield select((state: RootState) => state.core.player.state.video);
      if (sceneAsset.fallback && videoState.play === VideoPlayState.ERROR) {
        const config: ISceneConfig = yield select(state => selectSceneConfig(state, undefined, undefined));
        const configuredUrl = config?.start ? `${sceneAsset.fallback}#t=${config.start}` : sceneAsset.fallback;
        yield call([DashPlayer, DashPlayer.attachSource], configuredUrl);
      }
    }

    if (event.type === 'playbackTimeUpdated') {
      // Gate on `ready` for the same reason as manageVideo's end-detection: until
      // startVideo() has positioned the new source, dash's time() can still report
      // the previous video's playhead, which on a shorter `end` would fire a false
      // endVideo() → the freshly-attached slide is skipped. A real end only ever
      // arrives while ready+playing.
      const { ended, ready, outsideRegion } = yield select((state: RootState) => state.core.player.state.video);
      if (!ended && ready && !outsideRegion) {
        const actionEndTime: number | null = yield select(state => selectActionEndTime(state, undefined));
        if (actionEndTime != null && DashPlayer.time() >= actionEndTime) yield endVideo();
      }
    }
  }
  // Critical: Close the event channel to remove all event listeners
  videoEvents.close();
}

export function* removeVideo(cause = null) {
  //console.warn('removeVideo', cause);

  const videoState: IVideoState = yield select((state: RootState) => state.core.player.state.video);

  if (videoState.assetId) yield put(yield call(unloadAsset, videoState.assetId));

  const videoEl: HTMLVideoElement = document.getElementById('videoEl') as HTMLVideoElement;

  if (DashPlayer && DashPlayer.isReady()) {
    DashPlayer.pause();
  } else if ((isIOS || isSafari) && videoEl.src) {
    videoEl.src = '';
    videoEl.load();
  }
  endedAssetId = null;
  yield put(
    yield call(updateVideoState, {
      assetId: null,
      play: VideoPlayState.PAUSED,
      ready: false,
      ended: false,
      outsideRegion: false,
    }),
  );
}

function* updatePlayerStateSaga(action: PayloadAction<Partial<IPlayerState>>) {
  const videoEl: HTMLVideoElement = document.getElementById('videoEl') as HTMLVideoElement;
  const videoState: VideoPlayState = yield select((state: RootState) => state.core.player.state.video.play);
  const scene: ISceneDAO = yield select(state => selectScene(state, undefined));
  const sceneConfig: ISceneConfig = yield select(state => selectSceneConfig(state, undefined, undefined));
  const voiceSceneConfig: ISceneConfig = yield select(state => selectVoiceSceneConfig(state, undefined, undefined));
  const config = voiceSceneConfig ?? sceneConfig;

  const actionVoiceSceneId = yield select(state => selectActionVoiceSceneId(state, undefined));

  if (action.payload.paused === undefined || !config || (!scene && !actionVoiceSceneId)) return;
  // console.warn('cannot play 1', {
  //   paused: action.payload.paused,
  //   noConfig: !config,
  //   noScene: !scene && !actionVoiceSceneId,
  // });

  if (!action.payload.paused) {
    // While previewing outside the region the play button plays on from where
    // the user clicked, so it's the source video's end that is the dead end
    // here, not the clip's.
    const { outsideRegion } = yield select((state: RootState) => state.core.player.state.video);
    const stopAt = outsideRegion ? videoEl.duration : config.end || videoEl.duration;

    if (
      (!actionVoiceSceneId && scene.type !== SceneType.VIDEO) ||
      !videoEl.src ||
      !videoEl.src.length ||
      Math.abs(videoEl.currentTime - stopAt) < 0.1
    )
      return;
    // console.warn('cannot play 2', {
    //   invalidSceneType: !actionVoiceSceneId && scene.type !== SceneType.VIDEO,
    //   noSrc: !videoEl.src,
    //   noSrcLength: !videoEl.src.length,
    //   nearEnd: Math.abs(videoEl.currentTime - (config.end || videoEl.duration)) < 0.1,
    // });
  }

  try {
    if (
      ![VideoPlayState.PAUSED, VideoPlayState.START_PAUSING].includes(videoState) &&
      action.payload.paused &&
      !videoEl.paused
    ) {
      yield put(yield call(updateVideoState, { play: VideoPlayState.START_PAUSING }));
      yield videoEl.pause();
    }
    if (
      ![VideoPlayState.PLAYING, VideoPlayState.START_PLAYING].includes(videoState) &&
      action.payload.paused === false &&
      videoEl.paused
    ) {
      yield put(yield call(updateVideoState, { play: VideoPlayState.START_PLAYING }));
      // The play() promise must be observed. It rejects routinely — autoplay
      // policy, or "interrupted by a call to pause()" when startVideo's
      // `{ paused: true }` lands on a still-pending play — and the rejection used
      // to be swallowed whole by the outer catch, leaving `play` parked on
      // START_PLAYING. Since the guard above treats START_PLAYING as "already
      // starting", every later `paused: false` (canPlayVideo, controls, scene
      // resume) became a no-op and the learner sat on a paused video forever.
      // Rewinding to PAUSED puts the state back in sync with the element so the
      // next resume request is actually issued.
      try {
        yield videoEl.play();
      } catch (e) {
        const current: IVideoState = yield select((state: RootState) => state.core.player.state.video);
        // Only rewind if nothing has moved on since — a late rejection from a
        // superseded play() must not stomp a play that has since succeeded.
        if (current.play === VideoPlayState.START_PLAYING && videoEl.paused)
          yield put(yield call(updateVideoState, { play: VideoPlayState.PAUSED, buffering: false }));
      }
    }
  } catch (e) {}
}

function* manageVideo(asset?: IAsset, recoveryAttempt = 0) {
  const videoEl: HTMLVideoElement = document.getElementById('videoEl') as HTMLVideoElement;
  const videoEvents = yield call(videoPlaybackEvents);
  const assetId = yield select((state: RootState) => state.core.player.state.video.assetId);
  let aId = assetId;
  const generation = ++manageGeneration;

  // Counts consecutive fully-silent watchdog windows (no playback event at all).
  // Any real event resets it, so it only climbs while the element is wedged.
  let staleWindows = 0;
  // Resume bookkeeping for a ready video that stopped on its own (recoverStalledPlayback).
  const stall = { attempts: 0, resumedAt: NaN };
  // Where the playhead was on the last poll tick, and since when (see the frozen-end check).
  const progress = { time: NaN, since: 0 };

  // `ready` is only ever set by startVideo(), and the loop below can only call it
  // in response to a canplay/progress/seeked event. On a *revisit* the asset is
  // already LOADED, so loadDash's wait loop never blocks and it runs
  // attachView/attachSource synchronously — before this channel exists. Those
  // events fire into the void, the element then parks at readyState 4 with
  // nothing left to emit, and `ready` stays false forever → selectProceed pins on
  // LOADING (the stuck spinner going back-then-forward; the first visit works
  // only because the asset load yields long enough for the channel to be up).
  // So don't wait for an event that already happened: if the element is already
  // playable, kick startVideo now. Doing it here rather than in loadDash means
  // any seek it triggers is observed by the loop below.
  const { ready: alreadyReady } = yield select((state: RootState) => state.core.player.state.video);

  if (!alreadyReady && videoEl && videoEl.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) yield startVideo();

  while (assetId === aId && generation === manageGeneration) {
    // Race the next playback event against a watchdog timer. `ready` is only ever
    // set by startVideo(), which the handlers below reach in response to a
    // canplay/progress/seeked event. dash can wedge on a preloaded-player reattach
    // — readyState parks at 1 and it emits *no* event — so the loop would block on
    // `take` forever, `ready` stays false, and selectProceed pins the next button
    // on LOADING permanently (learners can't advance; admins bypass that gate).
    // The watchdog gives us a way to notice "nothing is happening and we're not
    // ready" and force recovery instead of blocking indefinitely.
    const { event, watchdog }: { event?: Event; watchdog?: true } = yield race({
      event: take(videoEvents),
      watchdog: delay(READY_WATCHDOG_MS),
    });

    // A newer manageVideo owns the element now (the source was reloaded).
    if (generation !== manageGeneration) break;

    if (watchdog) {
      const wedged: IVideoState = yield select((state: RootState) => state.core.player.state.video);
      // Mirror the event branch: track the live assetId so a scene swap ends this
      // loop via the `while` guard instead of us managing a video we no longer own.
      aId = wedged.assetId;
      // Asset swapped out from under us — stand down.
      if (assetId !== aId) {
        staleWindows = 0;
        continue;
      }

      // A ready video can still be wedged by its own flags. `buffering` latches true
      // on `waiting` and is only cleared by a `playing` event, and `play` latches on
      // START_PLAYING until that same event — so a stall that resolves without one
      // leaves selectProceed pinned on LOADING (spinner, dead next button) over a
      // video that is demonstrably playable, and leaves updatePlayerStateSaga
      // refusing every later resume because the state claims playback already
      // started. A silent window with the element holding data is proof no such
      // event is coming: reconcile the flags with the element and let canPlayVideo
      // decide whether playback should resume (it re-checks the viewer/interaction
      // gates, so a legitimately paused video stays paused).
      if (wedged.ready && videoEl && videoEl.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
        const stuckBuffering = wedged.buffering;
        const stuckStarting = wedged.play === VideoPlayState.START_PLAYING && videoEl.paused;

        if (stuckBuffering || stuckStarting) {
          staleWindows = 0;
          console.warn(
            `[video watchdog] clearing stale playback flags: asset=${aId} play=${wedged.play} ` +
              `buffering=${wedged.buffering} readyState=${videoEl.readyState} paused=${videoEl.paused}`,
          );
          yield put(
            yield call(updateVideoState, {
              ...(stuckBuffering ? { buffering: false } : {}),
              ...(stuckStarting ? { play: VideoPlayState.PAUSED } : {}),
            }),
          );
          if (stuckStarting) yield call(canPlayVideo);
          // Flags are honest again — judge the element itself next window, rather than
          // stacking recoverStalledPlayback's resume on top of canPlayVideo's.
          continue;
        }
      }

      // Only ever recover the video belonging to the CURRENT action. While a slide
      // transitions, the outgoing scene's source is deliberately left attached so
      // its last frame stays visible (see the deferred teardown in updateStateSaga
      // / viewerRestSaga). Nudging that stale video back to life could drive it to
      // its end and fire a spurious endVideo → next, skipping the slide the learner
      // just landed on — most visible on image actions, which have no video of
      // their own for the watchdog to be about.
      const currentAsset: IAsset = yield select(state => selectSceneAsset(state, undefined));
      if (!currentAsset || currentAsset.id !== aId) {
        staleWindows = 0;
        continue;
      }

      // Ready, yet silent for a whole window: a playing element floods timeupdate, so
      // it has stopped. Fine if we paused it; if the player still thinks it's playing
      // it stopped on its own and nothing else will notice.
      if (wedged.ready) {
        staleWindows = 0;
        yield recoverStalledPlayback(videoEl, stall);
        continue;
      }

      staleWindows += 1;

      // First few silent windows: nudge with startVideo(). With metadata present
      // (readyState >= 1) it seeks to `start`, which forces dash to request the
      // start segment and unwedges the stalled scheduler; if the element is
      // already playable it just flips `ready` true.
      if (staleWindows <= READY_WATCHDOG_NUDGES && videoEl.readyState >= HTMLMediaElement.HAVE_METADATA) {
        console.warn(
          `[video watchdog] nudging wedged video: asset=${aId} readyState=${videoEl.readyState} ` +
            `networkState=${videoEl.networkState} currentTime=${videoEl.currentTime.toFixed(2)} ` +
            `window=${staleWindows}/${READY_WATCHDOG_NUDGES} reload=${recoveryAttempt}`,
        );
        yield startVideo();
        continue;
      }

      // Persistent wedge (no decodable data, or nudges didn't take): rebuild the
      // source from scratch, bounded so an unplayable asset can't loop forever.
      if (asset && recoveryAttempt < READY_WATCHDOG_MAX_RELOADS) {
        console.warn(
          `[video watchdog] rebuilding wedged video source: asset=${aId} readyState=${videoEl.readyState} ` +
            `networkState=${videoEl.networkState} mediaError=${videoEl.error?.code ?? 'none'} ` +
            `reload=${recoveryAttempt + 1}/${READY_WATCHDOG_MAX_RELOADS}`,
        );
        videoEvents.close();
        yield removeVideo('ready watchdog - wedged reload');
        yield loadSceneVideo(asset, 'ready watchdog', recoveryAttempt + 1);
        return;
      }
      // Exhausted recovery — the learner is likely stuck. Loud, searchable marker
      // that pairs with the video diagnostics in any report captured from here.
      console.error(
        `[video watchdog] gave up on wedged video: asset=${aId} readyState=${videoEl.readyState} ` +
          `networkState=${videoEl.networkState} mediaError=${videoEl.error?.code ?? 'none'} ` +
          `after ${recoveryAttempt} reload(s)`,
      );
      continue;
    }

    staleWindows = 0;
    if (!event) continue;

    const videoState: IVideoState = yield select((state: RootState) => state.core.player.state.video);
    aId = videoState.assetId;

    // The (re)attach is finished once the element reports it can play — from here
    // an `emptied` genuinely means the source was lost, not a dash reattach.
    if (
      [VideoEvents.PLAYING, VideoEvents.CANPLAY, VideoEvents.CANPLAYTHROUGH, VideoEvents.LOADEDDATA].includes(
        event.type as VideoEvents,
      )
    )
      attachingSource = false;

    // Standing still while paused or across a seek isn't freezing: restart the measure,
    // or the first tick after a resume near the end would read as long since frozen.
    if ([VideoEvents.PLAY, VideoEvents.PLAYING, VideoEvents.SEEKED].includes(event.type as VideoEvents))
      progress.time = NaN;

    switch (event.type) {
      case VideoEvents.PLAYING:
        if (![VideoPlayState.PLAYING].includes(videoState.play))
          yield put(yield call(updateVideoState, { play: VideoPlayState.PLAYING, buffering: false, seeking: false }));
        break;
      case VideoEvents.PAUSE:
        if (![VideoPlayState.PAUSED].includes(videoState.play))
          yield put(yield call(updateVideoState, { play: VideoPlayState.PAUSED }));
        break;
      case VideoEvents.WAITING:
        if (![VideoPlayState.BUFFERING].includes(videoState.play))
          yield put(yield call(updateVideoState, { play: VideoPlayState.BUFFERING, buffering: true }));
        break;
      case VideoEvents.EMPTIED:
        if (
          !attachingSource &&
          ![VideoPlayState.SEEKING, VideoPlayState.BUFFERING].includes(videoState.play) &&
          videoState.ready
        )
          if (videoState.assetId) yield fork(confirmEmptied, videoEl, videoState.assetId);
        break;
    }

    const { ready } = yield select((state: RootState) => state.core.player.state.video);

    // startVideo() is the only place `ready: true` is set. This used to demand
    // readyState === 4 (HAVE_ENOUGH_DATA) *and* one of four specific event types.
    // dash streams with manifest gaps ("Adjusting playback time … gap in the
    // manifest"), which parks the element at readyState 3 (HAVE_FUTURE_DATA —
    // playable, but no promise of 4). It's perfectly playable, just never that one
    // exact value, so startVideo was never called at all: `ready` stayed false and
    // selectProceed pinned the next button on LOADING forever.
    //
    // Any event is a fine moment to ask "is it playable yet?" — HAVE_FUTURE_DATA
    // is the standard "can play forward from here" threshold, and startVideo does
    // the rest (seeks to `start` and only flips `ready` once positioned).
    if (!ready && videoEl.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) yield startVideo();

    // Re-read after startVideo() — it may have just flipped `ready` true (element
    // already at `start`) or kicked a seek that leaves `ready` false (any other
    // position). We gate end-detection on the *current* value, not the pre-call one.
    const { ended, ready: readyNow, outsideRegion } = yield select((state: RootState) => state.core.player.state.video);

    // A video can only legitimately end once it is `ready` — i.e. startVideo() has
    // confirmed it's positioned at `start` and playable. Until then the shared
    // #videoEl, mid source-switch, briefly reports the PREVIOUS video's
    // currentTime/duration; on an action whose `end` is smaller than that stale
    // position, end-detection would fire a false endVideo() → next → the new slide
    // is skipped the instant its video attaches. Gating on `ready` closes that
    // window (a real end always happens while ready+playing); the position/seek
    // guards below remain as a second line of defence.
    if (!ended && readyNow) {
      if ([VideoEvents.ENDED].includes(event.type as VideoEvents)) yield endVideo();

      const actionEndTime: number | null = yield select(state => selectActionEndTime(state, undefined));

      // Clicking the editor's waveform outside this action's region parks the
      // playhead on a part of the source video the clip never plays, to preview
      // it. Stopping at the region's end there would end the video the instant
      // playback started (the playhead is already past it), so the region's end
      // only applies again once the playhead is back inside the region — which
      // is also what puts Restart back on the play button at the clip's end.
      let previewing = !!outsideRegion;
      if (previewing) {
        const previewConfig: ISceneConfig = yield select(state => selectSceneConfig(state, undefined, undefined));
        const previewVoiceConfig: ISceneConfig = yield select(state =>
          selectVoiceSceneConfig(state, undefined, undefined),
        );
        const regionStart = (previewVoiceConfig ?? previewConfig)?.start ?? 0;
        const regionEnd = actionEndTime ?? videoEl.duration;

        if (videoEl.currentTime >= regionStart && videoEl.currentTime <= regionEnd) {
          previewing = false;
          yield put(yield call(updateVideoState, { outsideRegion: false }));
        }
      }

      const end = previewing ? videoEl.duration : actionEndTime ?? videoEl.duration;

      // Only treat currentTime >= end as "ended" when the position is trustworthy.
      // While switching sources (e.g. returning to a video via an action tile) the
      // element is mid-seek and briefly reports a stale currentTime carried over
      // from the previous video; if that stale value is past this action's `end`
      // we fire a false endVideo() → in admin the player pauses on the black intro
      // frame. Skip the check while seeking or when currentTime is outside the
      // current source's valid duration.
      const duration = videoEl.duration;
      const positionIsReliable =
        !videoEl.seeking && Number.isFinite(duration) && videoEl.currentTime <= duration + 0.25;

      if (Number.isFinite(end) && end > 0 && positionIsReliable && videoEl.currentTime >= end) yield endVideo();

      // The element can also stop just short of `end` on its own — dash starving a hair
      // before a declared duration, iOS pausing at the source's fragment end — and then
      // emits nothing more (the timeupdate poll only ticks while playing), so the check
      // above never sees currentTime reach `end`. A stop we didn't ask for, at the end
      // of what can be played, is the end.
      if ([VideoEvents.PAUSE, VideoEvents.WAITING].includes(event.type as VideoEvents)) {
        const playerPaused: boolean = yield select((state: RootState) => state.core.player.state.paused);
        const stoppedAtEnd: boolean = playerPaused ? false : yield isStoppedAtEnd(videoEl);
        if (stoppedAtEnd) yield endVideo();
      }

      // ...or freeze there without pausing at all. Then no pause/waiting arrives, and the
      // timeupdate poll (which ticks while not paused) keeps the watchdog from ever seeing
      // a silent window — so only the playhead not moving gives it away.
      if (event.type === VideoEvents.TIMEUPDATE && !videoEl.paused) {
        if (playheadStillFor(progress, videoEl.currentTime, Date.now()) >= FROZEN_MS) {
          const playerPaused: boolean = yield select((state: RootState) => state.core.player.state.paused);
          const frozenAtEnd: boolean = playerPaused ? false : yield isStoppedAtEnd(videoEl, FROZEN_END_TOLERANCE, true);
          if (frozenAtEnd) {
            console.warn(
              `[video] ending video frozen at its end: asset=${aId} ` +
                `currentTime=${videoEl.currentTime.toFixed(2)} playableEnd=${playableEnd(videoEl)}`,
            );
            yield endVideo();
          }
        }
      }
    }
  }

  videoEvents.close();
}

// dash also empties the element when it re-attaches its own source mid-play (e.g. after
// "Failed to set append window"), and that can land after the `playing` that ended the
// attach guard. The element has a source again straight after, and plays on — but
// treating it as lost nulls `assetId`, which hides the video: black over a playing
// video, for good. So only call it lost if there is still no source a moment later.
function* confirmEmptied(videoEl: HTMLVideoElement, assetId: string) {
  yield delay(EMPTIED_GRACE);

  const videoState: IVideoState = yield select((state: RootState) => state.core.player.state.video);
  if (videoState.assetId !== assetId || videoEl.currentSrc) return;

  yield put(
    yield call(updateVideoState, {
      play: VideoPlayState.EMPTIED,
      assetId: null,
      buffering: false,
      seeking: false,
    }),
  );
}

// End of the range the element can actually play: its duration, or the `#t=…,end`
// fragment on a native (iOS/Safari) src, whichever is sooner. NaN if unknown.
function playableEnd(videoEl: HTMLVideoElement): number {
  const fragmentEnd = Number((videoEl.currentSrc || videoEl.src || '').match(/#t=[^,]*,([\d.]+)/)?.[1]);
  const ends = [videoEl.duration, fragmentEnd].filter(Number.isFinite);
  return ends.length ? Math.min(...ends) : NaN;
}

// The element has stopped (paused, or starved of data) at or just short of the
// current action's end, or the end of what it can play at all. `frozen`: the caller
// has seen the playhead stand still, so a not-paused element counts as stopped too.
function* isStoppedAtEnd(videoEl: HTMLVideoElement, tolerance = STALLED_END_TOLERANCE, frozen = false) {
  if (videoEl.seeking) return false;
  if (!frozen && !videoEl.paused && videoEl.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) return false;

  const actionEndTime: number | null = yield select(state => selectActionEndTime(state, undefined));
  const end = Math.min(...[actionEndTime, playableEnd(videoEl)].filter(Number.isFinite));

  return Number.isFinite(end) && end > 0 && videoEl.currentTime >= end - tolerance;
}

// A `ready` video has gone silent while the player may still think it is playing.
// Left alone, the next button waits on an end that never comes and the play button
// (which reads Redux, not the element) toggles nothing. So: if it stopped at its end,
// end it; otherwise retry play() a couple of times, then pause the player properly so
// the play button shows and the learner can resume it themselves.
function* recoverStalledPlayback(videoEl: HTMLVideoElement, stall: { attempts: number; resumedAt: number }) {
  const { paused, video, viewer, controls }: IPlayerState = yield select((state: RootState) => state.core.player.state);
  const mode: Mode = yield select(selectSessionMode);
  const actionEditor: IActionEditorState = yield select(selectActionEditor);

  if (paused || video.ended || mode === Mode.ADMIN || actionEditor?.enabled) return;
  // Mirror canPlayVideo's gate; a backgrounded tab pausing the element isn't ours to undo.
  if (viewer.state !== ViewerState.RESTING || controls.animating || document.visibilityState !== 'visible') return;

  const stoppedAtEnd: boolean = yield isStoppedAtEnd(videoEl);
  if (stoppedAtEnd) {
    console.warn(
      `[video watchdog] ending video stalled at its end: asset=${video.assetId} ` +
        `currentTime=${videoEl.currentTime.toFixed(2)} playableEnd=${playableEnd(videoEl)}`,
    );
    yield endVideo();
    return;
  }

  // Not paused means it's still trying to play (buffering) — leave that to the element.
  if (!videoEl.paused) return;

  // Progress since the last resume means it took; a stop at the same spot means it didn't.
  if (Math.abs(videoEl.currentTime - stall.resumedAt) > 1) stall.attempts = 0;

  if (stall.attempts < STALLED_RESUME_ATTEMPTS) {
    stall.attempts += 1;
    stall.resumedAt = videoEl.currentTime;
    console.warn(
      `[video watchdog] resuming video that stopped on its own: asset=${video.assetId} ` +
        `readyState=${videoEl.readyState} currentTime=${videoEl.currentTime.toFixed(2)} ` +
        `attempt=${stall.attempts}/${STALLED_RESUME_ATTEMPTS}`,
    );
    // Resume through updatePlayerStateSaga rather than calling play() here: it runs in
    // its own task (a never-settling play() on a stalled load can't park this loop) and
    // rewinds `play` to PAUSED if play() rejects, so the next window sees honest state.
    yield put(yield call(updatePlayerState, { paused: false, culprit: 'video watchdog' }));
    return;
  }

  console.error(
    `[video watchdog] could not resume video, showing play button: asset=${video.assetId} ` +
      `readyState=${videoEl.readyState} currentTime=${videoEl.currentTime.toFixed(2)}`,
  );
  stall.attempts = 0;
  stall.resumedAt = NaN;
  yield put(yield call(updatePlayerState, { paused: true, userPaused: true, culprit: 'video watchdog' }));
}

function* startVideo(newVideo = true) {
  const { ready } = yield select((state: RootState) => state.core.player.state.video);
  const videoState: IVideoState = yield select((state: RootState) => state.core.player.state.video);
  const videoEl: HTMLVideoElement = document.getElementById('videoEl') as HTMLVideoElement;
  const sceneConfig: ISceneConfig = yield select(state => selectSceneConfig(state, undefined, undefined));
  const voiceSceneConfig: ISceneConfig = yield select(state => selectVoiceSceneConfig(state, undefined, undefined));
  const action: IActionBasic = yield select(state => selectAction(state, undefined));

  const config = action?.voiceSceneConfigId ? voiceSceneConfig : sceneConfig;

  if (!config) return console.warn('cant play video - no config');

  const start = config.start || 0.1;

  if (!videoEl) return console.warn('cant play video - no video');

  if (videoState.ended || videoState.outsideRegion) {
    endedAssetId = null;
    yield put(yield call(updateVideoState, { ended: false, outsideRegion: false }));
  }

  // A paused slide holds whatever frame the video is on, unless its start has been
  // set to somewhere further on. Seeking back to a start that was never chosen (the
  // 0.1 default) or one already played past jumped a paused slide back to an earlier
  // frame — or the very first one — every time the readiness checks re-ran this.
  const holdsFrame =
    action?.behaviour === ActionBehaviour.PAUSE &&
    !action.voiceSceneId &&
    (config.start == null || config.start < videoEl.currentTime);

  if (holdsFrame || Math.abs(videoEl.currentTime - start) < 0.1) {
    // `newVideo` gates the pause, not the readiness flag. This is the only place
    // `ready: true` is ever set, and the revisit path (updateStateSaga, ACTION,
    // same scene already attached) calls startVideo(false) — so with the element
    // already sitting at `start` this branch used to set nothing at all. Combined
    // with loadSceneVideo's unconditional `ready: false`, that left `ready` stuck
    // false forever and selectProceed pinned on LOADING (the spinner on a video
    // you've already visited). A video positioned at `start` and playable is
    // ready no matter how we got here.
    if (!ready) {
      // Never pause into a play() that hasn't resolved yet. This pause used to
      // fire unconditionally, and when a buffering stall reordered things so the
      // element was mid-`play()` (play state START_PLAYING), it aborted that play
      // with "interrupted by a call to pause()" — wedging the player (see the
      // rejection handler in updatePlayerStateSaga).
      if (newVideo && ![VideoPlayState.START_PLAYING, VideoPlayState.PLAYING].includes(videoState.play))
        yield put(yield call(updatePlayerState, { paused: true, culprit: 1 }));
      // Clear `buffering` alongside `ready`: it latches true on the `waiting`
      // event and is otherwise only cleared by a `playing` event, so a stall that
      // resolves without one left selectProceed pinned on LOADING (spinner + dead
      // next button) on a video that is, by this point, demonstrably playable.
      yield put(yield call(updateVideoState, { ready: true, buffering: false }));
    }
  } else {
    if (ready) yield put(yield call(updateVideoState, { ready: false }));

    yield put(yield call(updateVideoState, { play: VideoPlayState.SEEKING, seeking: true }));

    videoEl.currentTime = start;
    return;
  }

  if (videoEl.volume !== config.volume || videoEl.muted) {
    //if (videoState.interacted)
    videoEl.muted = config.volume === 0;
    videoEl.volume = config.volume || 1;
  } else videoEl.muted = false;
}

function* endVideo() {
  const sceneConfig: ISceneConfig = yield select(state => selectSceneConfig(state, undefined, undefined));
  const voiceSceneConfig: ISceneConfig = yield select(state => selectVoiceSceneConfig(state, undefined, undefined));
  const config = voiceSceneConfig ?? sceneConfig;
  const videoEl: HTMLVideoElement = document.getElementById('videoEl') as HTMLVideoElement;
  const action: IActionBasic = yield select(state => selectAction(state, undefined));

  const videoState: IVideoState = yield select((state: RootState) => state.core.player.state.video);

  // Looping and pause-behaviour describe how the *clip* ends. A preview of the
  // rest of the source video isn't the clip, so when it reaches the end of the
  // video it just ends — which is what turns the play button back into Restart.
  if (!videoState.outsideRegion) {
    if (config && config.loop) {
      const start = config.start || 0;
      videoEl.currentTime = start;
      return;
    }
    if (action && action.behaviour === ActionBehaviour.PAUSE && !action.voiceSceneId) return;
  }

  // Idempotency guard: bail if this asset's end has already been handled, even if
  // the `ended` flag hasn't committed yet (concurrent end detectors race here).
  if (videoState.ended || (videoState.assetId != null && endedAssetId === videoState.assetId)) return;
  endedAssetId = videoState.assetId;

  yield put(yield call(updateVideoState, { ended: true }));
}

function* updateStateSaga(action: PayloadAction<Partial<IUpdateState>>) {
  // const ignore = yield select(selectIgnoreActions);
  // if (ignore) return;

  switch (action.payload.type) {
    case EntityTypes.SCENE:
      let sceneAsset: IAsset = yield select(state => selectSceneAsset(state, action.payload.id));

      if (sceneAsset && sceneAsset.type === AssetType.VIDEO && !(yield call(sceneVideoIsBackground)))
        yield loadSceneVideo(sceneAsset);

      break;
    case EntityTypes.ACTION: {
      // A preview outside the region belongs to the action whose region it was
      // measured against — the new action gets its own region back.
      const previewing: boolean = yield select((state: RootState) => state.core.player.state.video.outsideRegion);
      if (previewing) yield put(yield call(updateVideoState, { outsideRegion: false }));

      const sceneAsset: IAsset = yield select(state => selectSceneAsset(state, undefined));
      const action: IActionBasic = yield select(state => selectAction(state, undefined));
      const scene: ISceneDAO = yield select(state => selectScene(state, undefined));
      const hasVideo = (scene && scene.type === SceneType.VIDEO) || action?.voiceSceneId;

      if (action?.voiceSceneId) {
        const voiceSceneAsset: IAsset = yield select(state => selectSceneAsset(state, action.voiceSceneId));
        if (voiceSceneAsset && voiceSceneAsset.type === AssetType.VIDEO) {
          // Consecutive actions sharing a voiceover video play it straight through, the
          // same as actions sharing a video scene below: only seek, never reattach.
          const videoState: IVideoState = yield select((state: RootState) => state.core.player.state.video);
          const videoEl = document.getElementById('videoEl') as HTMLVideoElement;
          const attached =
            videoState.assetId === voiceSceneAsset.id &&
            loadedSource.assetId === voiceSceneAsset.id &&
            hasLiveSource(videoEl);
          const actionEnd: number | null = yield select(state =>
            selectActionSceneEnd(state, action.id, action.voiceSceneId),
          );
          const sourceTooShort =
            attached &&
            loadedSource.end != null &&
            (actionEnd == null || actionEnd > loadedSource.end + SOURCE_END_EPSILON);

          if (!attached) yield loadSceneVideo(voiceSceneAsset);
          else if (sourceTooShort) yield rebuildSource(voiceSceneAsset);
          else yield startVideo(false);
        }
      } else if (!hasVideo && action && (!action.scene || action.scene.id !== sceneAsset?.entityId)) {
        // The SCENE pointer (and thus the video mesh) is held until the viewer
        // settles — see removeSceneIfNotRequired in module/scene.ts. Tearing the
        // video source down here (mid slide-transition) would black out the frozen
        // frame that's still visible behind the outgoing slide. Instead pause the
        // element to freeze the last frame and defer removeVideo to RESTING
        // (viewerRestSaga). Only tear down immediately if we're already at rest
        // (e.g. admin, no slide animation).
        const viewerState: ViewerState = yield select((state: RootState) => state.core.player.state.viewer.state);
        if (viewerState === ViewerState.RESTING) {
          yield removeVideo('action has no video');
        } else {
          yield put(yield call(updatePlayerState, { paused: true, culprit: 'defer video teardown' }));
        }
      } else if (hasVideo && action?.scene && scene.id === action.sceneId) {
        // Same scene, so normally just a seek — unless this action ends past the end
        // baked into the loaded source, which the element can never play beyond
        // (selectSceneRunEnd usually prevents this; the feed can change under it).
        const actionEnd: number | null = yield select(state => selectActionSceneEnd(state, action.id, scene.id));
        const sourceTooShort =
          !!sceneAsset &&
          loadedSource.assetId === sceneAsset.id &&
          loadedSource.end != null &&
          (actionEnd == null || actionEnd > loadedSource.end + SOURCE_END_EPSILON);

        // Coming off a voiceover on this scene, #videoEl still holds the voiceover (the
        // scene video was on #bgVideoEl), so the scene video has to come back here.
        const loadedAssetId: string | null = yield select((state: RootState) => state.core.player.state.video.assetId);
        const holdsOtherVideo = !!sceneAsset && !!loadedAssetId && loadedAssetId !== sceneAsset.id;

        // A paused slide stays on the frame it was given, unless its start is further on.
        // Holding, the source's end doesn't matter to it: rebuilding there sent a wrong
        // answer back to the first frame, and the next action to play rebuilds if it must.
        const sceneConfig: ISceneConfig = yield select(state => selectSceneConfig(state, undefined, undefined));
        const videoEl = document.getElementById('videoEl') as HTMLVideoElement;
        const holdsFrame = action.behaviour === ActionBehaviour.PAUSE && !(sceneConfig?.start > videoEl.currentTime + 0.1);

        if (action.behaviour === ActionBehaviour.PAUSE) yield put(yield call(updatePlayerState, { paused: true }));

        if (holdsOtherVideo) yield loadSceneVideo(sceneAsset, 'scene video back from background');
        else if (holdsFrame) return;
        else if (sourceTooShort) yield rebuildSource(sceneAsset);
        else yield startVideo(false);
      } else {
        //This is a general pause I had to put in for admin.
        const paused = yield select((state: RootState) => state.core.player.state.paused);
        if (!paused) yield put(yield call(updatePlayerState, { paused: true, culprit: 'video.updateStateSaga' }));
      }
    }
  }
}

// Deferred counterpart to the pause in updateStateSaga: once the slide transition
// finishes, if the now-current action has no video but a source is still attached
// (kept alive so its last frame stayed visible during the animation), tear it down.
function* viewerRestSaga(action: PayloadAction<IViewer>) {
  if (action.payload.state !== ViewerState.RESTING) return;

  // Base hasVideo on the settled action's own scene, not the SCENE pointer — the
  // pointer swap (scene.ts) and this saga both fire on RESTING and their order
  // isn't guaranteed. The current action is already the destination here.
  const currentAction: IActionBasic = yield select(state => selectAction(state, undefined));
  const hasVideo =
    (currentAction?.scene && currentAction.scene.type === SceneType.VIDEO) || currentAction?.voiceSceneId;
  const videoState: IVideoState = yield select((state: RootState) => state.core.player.state.video);

  if (!hasVideo && videoState.assetId) yield removeVideo('viewer rest - no video');
}

function* prevSaga() {
  const { index }: IActionFeed = yield select(selectActionFeed);
  const scene: ISceneDAO = yield select(state => selectScene(state, undefined));
  if (index === 0 && scene?.videoId) yield startVideo();
}

// A source loaded outside the editor (e.g. preview, then Edit) still has its saved end
// baked in, which the element can never play past — so dragging the end out on the
// timeline would stop at the old end. Reload it without one, as editor loads are.
function* actionEditorEnabledSaga() {
  const actionEditor: IActionEditorState = yield select(selectActionEditor);
  if (!actionEditor?.enabled || loadedSource.end == null) return;

  const voiceSceneId: number | undefined = yield select(state => selectActionVoiceSceneId(state, undefined));
  const asset: IAsset = yield select(state => selectSceneAsset(state, voiceSceneId ?? undefined));
  const videoState: IVideoState = yield select((state: RootState) => state.core.player.state.video);

  if (asset?.type === AssetType.VIDEO && loadedSource.assetId === asset.id && videoState.assetId === asset.id)
    yield rebuildSource(asset, 'editor enabled over a source with an end');
}

function* watchActionEditorEnabled() {
  yield takeEvery(actionEditorActions.enable, actionEditorEnabledSaga);
}

function* watchAssetLoaded() {
  yield takeEvery(loadAsset.fulfilled, assetLoaded);
}

function* watchPlayerState() {
  yield takeEvery(updatePlayerState.type, updatePlayerStateSaga);
}

function* watchUpdateState() {
  yield takeEvery(updateState.type, updateStateSaga);
}

function* watchPrev() {
  yield takeEvery(prev, prevSaga);
}

function* watchViewerRest() {
  yield takeEvery(updateViewerState.type, viewerRestSaga);
}

// Any explicit `ended: false` (e.g. the timeline editor seeking/resizing a
// region) means the video should be allowed to end again. Clear the dedupe
// guard so a subsequent end at the newly-edited end time isn't swallowed —
// without this, changing a video's end time and seeking leaves the guard
// pinned to the asset and the video plays past the new end.
function resetEndGuardSaga(action: PayloadAction<Partial<IVideoState>>) {
  if (action.payload.ended === false) endedAssetId = null;
}

function* watchResetEndGuard() {
  yield takeEvery(updateVideoState.type, resetEndGuardSaga);
}

export function* videoSaga() {
  yield all([
    fork(watchUpdateState),
    fork(watchPlayerState),
    fork(watchAssetLoaded),
    fork(watchPrev),
    fork(watchViewerRest),
    fork(watchResetEndGuard),
    fork(watchActionEditorEnabled),
  ]);
}
